/**
 * FRONT-2509: a Realtime voice call runs straight between the browser and
 * OpenAI, so the only way its cost reaches Eddy's ledger is the browser
 * forwarding OpenAI's own per-response usage. Pure on purpose: the frontend
 * suite has no DOM, so the event reading and the request shapes are pinned
 * here and the hook only wires them.
 *
 * Nothing but counts leaves the browser — the response id (for dedupe) and
 * the `usage` block. No transcript, no audio.
 */
import { timezoneHeader } from '~/lib/timezone';

export interface RealtimeUsageReport {
  responseId: string;
  usage: Record<string, unknown>;
}

/**
 * The usage to report for one inbound data-channel event, or null. Only a
 * finished response carries it (`response.done`, `response.usage`); every
 * other event, and a response.done without a usage block, is nothing to do.
 */
export function realtimeUsageReportOf(event: unknown): RealtimeUsageReport | null {
  if (!event || typeof event !== 'object') return null;
  const e = event as { type?: unknown; response?: { id?: unknown; usage?: unknown } };
  if (e.type !== 'response.done') return null;
  const id = e.response?.id;
  const usage = e.response?.usage;
  if (typeof id !== 'string' || !id) return null;
  if (!usage || typeof usage !== 'object' || Array.isArray(usage)) return null;
  return { responseId: id, usage: usage as Record<string, unknown> };
}

/**
 * `keepalive` for both: the last response's report and the hang-up often
 * fire as the call tears down or the tab navigates away, exactly when a
 * plain fetch can be cancelled mid-flight.
 */
export function buildRealtimeUsageRequest(
  callId: string,
  report: RealtimeUsageReport,
): { url: string; init: RequestInit } {
  return {
    url: '/api/voice/realtime/usage',
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
      body: JSON.stringify({ callId, responseId: report.responseId, usage: report.usage }),
      keepalive: true,
    },
  };
}

/**
 * `connected` says the call reached OpenAI at all: only then does a call
 * that reported no response get billed from its length.
 */
export function buildRealtimeEndedRequest(callId: string, connected: boolean): { url: string; init: RequestInit } {
  return {
    url: '/api/voice/realtime/usage/end',
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...timezoneHeader() },
      body: JSON.stringify({ callId, connected }),
      keepalive: true,
    },
  };
}

/** The cost anchor the mint hands back, or null for a server that predates it. */
export function usageCallIdOf(mintResponse: unknown): string | null {
  if (!mintResponse || typeof mintResponse !== 'object') return null;
  const id = (mintResponse as { usageCallId?: unknown }).usageCallId;
  return typeof id === 'string' && id ? id : null;
}
